import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Await } from 'react-router';
import { useTimeout } from 'usehooks-ts';
import cn from '@/utils/merge-class-names';

interface Props<T> {
  promise?: Promise<T> | boolean;
  timeout?: number;
  className?: string;
  overlay?: boolean;
  children?: (data?: Nullable<T>) => ReactNode;
}

export default function LoadingLayout<T>({ promise, className, overlay, timeout, children }: Props<T>) {
  const fallbackElement = (
    <LoadingFallback
      overlay={overlay}
      timeout={timeout}
      className={className}
    />
  );

  if (promise === undefined) return fallbackElement;
  if (typeof promise === 'boolean' && promise) return fallbackElement;

  if (promise instanceof Promise) {
    return (
      <Suspense fallback={fallbackElement}>
        <Await resolve={promise}>{children}</Await>
      </Suspense>
    );
  }

  return typeof children === 'function' ? children() : children;
}

function LoadingFallback<T>({
  overlay = false,
  timeout = 6_000,
  className,
  children,
}: Pick<Props<T>, 'className' | 'overlay' | 'timeout' | 'children'>) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isTimedout, setTimedout] = useState(false);

  useTimeout(() => setTimedout(true), isTimedout ? null : timeout);

  const handleRefresh = () => {
    setTimedout(false);
    return queryClient.resetQueries();
  };

  return (
    <div className={cn('grid grid-areas-stack flex-1', className)}>
      {overlay && (
        <div className='grid-in-stack size-full'>{typeof children === 'function' ? children() : children}</div>
      )}
      <div className={cn('grid-in-stack size-full flex flex-center', { 'backdrop-blur': !overlay })}>
        <div className='flex flex-col gap-y-24 flex-center'>
          <span className='loading loading-spinner loading-lg text-primary' />

          {isTimedout && (
            <button
              type='button'
              className='btn btn-primary btn-outline'
              onClick={handleRefresh}>
              {t('common.refresh')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
